import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, MapPin, Wrench, Droplets, Ruler, Shield, Save, ChevronDown, ChevronUp, Plus, Trash2, XCircle } from 'lucide-react';

const PUMP_TYPES = ['centrifugal', 'jet', 'submersible', 'turbine', 'other'];

const CASING_MATERIALS = [
  'pvc', 'black_steel', 'galvanized', 'stainless_steel', 'not_cased', 'other'
];
const CASING_MATERIAL_LABELS = {
  pvc: 'PVC', black_steel: 'Black Steel', galvanized: 'Galvanized',
  stainless_steel: 'Stainless Steel', not_cased: 'Not Cased', other: 'Other',
};

const SEAL_MATERIALS = ['bentonite', 'neat_cement', 'sand_cement', 'other'];
const GRAIN_SIZES = ['very_fine', 'fine', 'medium', 'coarse', 'very_coarse', 'na'];
const GRAIN_SIZE_LABELS = {
  very_fine: 'Very Fine', fine: 'Fine', medium: 'Medium',
  coarse: 'Coarse', very_coarse: 'Very Coarse', na: 'N/A',
};
const DATA_SOURCES = ['gps', 'map', 'survey'];
const DATUMS = ['nad_27', 'nad_83', 'wgs_84'];

const WELL_LOG_COLORS = [
  { value: 'black', label: 'Black' },
  { value: 'white', label: 'White' },
  { value: 'brown', label: 'Brown' },
  { value: 'green', label: 'Green' },
  { value: 'gray', label: 'Gray' },
  { value: 'red', label: 'Red' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'tan', label: 'Tan' },
  { value: 'na', label: 'N/A' },
];

const WELL_LOG_MATERIALS = [
  { value: 'top_soil', label: 'Top Soil' },
  { value: 'sand', label: 'Sand' },
  { value: 'clay', label: 'Clay' },
  { value: 'shell', label: 'Shell' },
  { value: 'rock', label: 'Rock' },
  { value: 'limestone', label: 'Limestone' },
  { value: 'sandstone', label: 'Sandstone' },
  { value: 'marl', label: 'Marl' },
  { value: 'gravel', label: 'Gravel' },
  { value: 'silt', label: 'Silt' },
  { value: 'coquina', label: 'Coquina' },
  { value: 'coral', label: 'Coral' },
  { value: 'phosphate', label: 'Phosphate' },
  { value: 'peat', label: 'Peat' },
  { value: 'fill', label: 'Fill' },
];

const WORK_TYPES = ['construction', 'repair', 'modification', 'abandonment'];

const DRILL_METHODS = [
  'rotary', 'cable_tool', 'auger', 'jetted', 'sonic',
  'combination', 'horizontal_drilling', 'hydraulic_point', 'other'
];
const DRILL_METHOD_LABELS = {
  rotary: 'Rotary', cable_tool: 'Cable Tool', auger: 'Auger',
  jetted: 'Jetted', sonic: 'Sonic',
  combination: 'Combination (Two or More Methods)',
  horizontal_drilling: 'Horizontal Drilling',
  hydraulic_point: 'Hydraulic Point (Direct Push)',
  other: 'Other',
};

const INTENDED_USES = [
  'domestic', 'bottled_water_supply',
  'public_water_supply_limited', 'public_water_supply_community',
  'agricultural_irrigation', 'landscape_irrigation',
  'commercial_industrial', 'livestock', 'nursery_irrigation',
  'golf_course_irrigation', 'recreation_area_irrigation',
  'monitoring', 'test', 'site_investigation',
  'earth_coupled_geothermal', 'hvac_supply', 'hvac_return',
  'class_i_injection',
  'class_v_recharge', 'class_v_recovery', 'class_v_air_sparge',
  'class_v_commercial_disposal', 'class_v_aquifer_storage', 'class_v_drainage',
  'remediation', 'other'
];
const INTENDED_USE_LABELS = {
  domestic: 'Domestic',
  bottled_water_supply: 'Bottled Water Supply',
  public_water_supply_limited: 'Public Water Supply (Limited Use/DOH)',
  public_water_supply_community: 'Public Water Supply (Community/DEP)',
  agricultural_irrigation: 'Agricultural Irrigation',
  landscape_irrigation: 'Landscape Irrigation',
  commercial_industrial: 'Commercial/Industrial',
  livestock: 'Livestock',
  nursery_irrigation: 'Nursery Irrigation',
  golf_course_irrigation: 'Golf Course Irrigation',
  recreation_area_irrigation: 'Recreation Area Irrigation',
  monitoring: 'Monitoring',
  test: 'Test',
  site_investigation: 'Site Investigation',
  earth_coupled_geothermal: 'Earth-Coupled Geothermal',
  hvac_supply: 'HVAC Supply',
  hvac_return: 'HVAC Return',
  class_i_injection: 'Class I Injection',
  class_v_recharge: 'Class V: Recharge',
  class_v_recovery: 'Class V: Recovery',
  class_v_air_sparge: 'Class V: Air Sparge',
  class_v_commercial_disposal: 'Class V: Commercial/Industrial Disposal',
  class_v_aquifer_storage: 'Class V: Aquifer Storage and Recovery',
  class_v_drainage: 'Class V: Drainage',
  remediation: 'Remediation',
  other: 'Other (Describe)',
};

const WMDS = [
  { value: 'swfwmd', label: 'Southwest' },
  { value: 'nwfwmd', label: 'Northwest' },
  { value: 'sjrwmd', label: 'St. Johns River' },
  { value: 'sfwmd', label: 'South Florida' },
  { value: 'srwmd', label: 'Suwannee River' },
];

const COMPANY_INFO = {
  name: 'Blue Well LLC',
  address: '3307 Baxter Ave, Sebring, FL 33870',
  phone: '(863) 658-0035',
  email: 'bluewell.llc@gmail.com',
  license: '9566',
};

const emptyCasingRow = () => ({ diameter: '', from: '', to: '', bags: '', seal_material: 'bentonite' });
const emptyAbandonmentRow = () => ({ from: '', to: '', bags: '', seal_material: 'neat_cement' });
const emptyCasingSection = () => ({ from_depth: 0, to_depth: 0, diameter: '2', material: 'pvc' });
const emptyCompletionInterval = () => ({ type: 'screen', from_depth: 0, to_depth: 0 });

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          {Icon && <Icon className="w-4 h-4 text-[#003366]" />} {title}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

export default function CompletionReportForm({ permit, project, contact, estimate, job, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    wmd: 'swfwmd',
    permit_number: '', cup_wup_number: '', did_number: '',
    florida_unique_id: '', delineation_number: '',
    wells_constructed: '1', wells_not_constructed: '0',
    owner_name: '', owner_email: '', completion_date: '',
    well_address: '', well_county: '',
    section: '', township: '', range_field: '', land_grant: '',
    latitude: '', longitude: '',
    data_source: 'gps', datum: 'nad_83',
    work_type: 'construction',
    intended_uses: [], other_use_description: '',
    drill_method: 'rotary',
    static_water_level: '', pumping_water_level: '', pumping_hours: '', pumping_gpm: '',
    measuring_point: '', measuring_point_ft: '', measuring_above_below: 'above',
    is_flowing: 'no',
    casing_material: 'pvc',
    total_well_depth: '', drilled_depth: '', cased_depth: '',
    open_hole_from: '', open_hole_to: '',
    screen_from: '', screen_to: '', slot_size: '',
    casing_sections: [emptyCasingSection()],
    completion_intervals: [emptyCompletionInterval()],
    abandonment_rows: [emptyAbandonmentRow()],
    surface_casing_rows: [emptyCasingRow()],
    primary_casing_rows: [emptyCasingRow()],
    liner_casing_rows: [emptyCasingRow()],
    telescope_casing_rows: [emptyCasingRow()],
    pump_type: 'submersible', pump_hp: '', pump_capacity: '', pump_depth: '', intake_depth: '',
    iron_ppm: '', sulfate_ppm: '', chloride_ppm: '', test_type: 'field',
    drill_cuttings: [{ from: '', to: '', color: 'black', grain_size: 'na', material: 'top_soil' }],
    contractor_name: COMPANY_INFO.name, contractor_license: COMPANY_INFO.license,
    contractor_email: COMPANY_INFO.email, driller_name: '',
    additional_notes: '',
  });

  useEffect(() => {
    const updates = {};
    if (permit?.completion_report && Object.keys(permit.completion_report).length > 0) {
      Object.assign(updates, permit.completion_report);
      if (updates.drill_cuttings && !Array.isArray(updates.drill_cuttings)) {
        updates.drill_cuttings = [{ from: '', to: '', color: 'black', grain_size: 'na', material: 'top_soil' }];
      }
      ['abandonment_rows', 'surface_casing_rows', 'primary_casing_rows', 'liner_casing_rows', 'telescope_casing_rows'].forEach(key => {
        if (updates[key] && !Array.isArray(updates[key])) delete updates[key];
      });
      // Backward compatibility: older completion reports only have the flat
      // cased_depth/open_hole/screen fields — build the new casing_sections
      // / completion_intervals arrays from them so the new UI still shows
      // something sensible instead of an empty box.
      if (!Array.isArray(updates.casing_sections) || updates.casing_sections.length === 0) {
        updates.casing_sections = [{
          from_depth: 0, to_depth: updates.cased_depth || '',
          diameter: updates.casing_diameter || '2', material: updates.casing_material || 'pvc',
        }];
      }
      if (!Array.isArray(updates.completion_intervals) || updates.completion_intervals.length === 0) {
        const ivs = [];
        if (updates.screen_from || updates.screen_to) ivs.push({ type: 'screen', from_depth: updates.screen_from || 0, to_depth: updates.screen_to || 0 });
        if (updates.open_hole_from || updates.open_hole_to) ivs.push({ type: 'open_hole', from_depth: updates.open_hole_from || 0, to_depth: updates.open_hole_to || 0 });
        updates.completion_intervals = ivs.length ? ivs : [emptyCompletionInterval()];
      }
      // Auto-fill the permit number from the linked permit when it's still blank
      // (page-1 permit number sourced from the sealed county permit number).
      if (!updates.permit_number) {
        updates.permit_number = permit?.county_permit_number || permit?.permit_number || permit?.permit_number_auto || '';
      }
    } else {
      updates.permit_number = permit?.county_permit_number || permit?.permit_number || permit?.permit_number_auto || '';
      if (contact) {
        updates.owner_name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
        updates.owner_email = contact.email || '';
      }
      if (project) {
        updates.well_address = project.location?.address || permit?.property_address || '';
        updates.well_county = project.location?.county || permit?.county || '';
        updates.latitude = project.location?.latitude || permit?.latitude || '';
        updates.longitude = project.location?.longitude || permit?.longitude || '';
        updates.section = project.location?.section || '';
        updates.township = project.location?.township || '';
        updates.range_field = project.location?.range || '';
      }
      if (permit) {
        updates.work_type = permit.type || 'construction';
        if (permit.well_type) updates.intended_uses = [permit.well_type];
        if (!updates.well_county) updates.well_county = permit.county || '';
        if (!updates.latitude) updates.latitude = permit.latitude || '';
        if (!updates.longitude) updates.longitude = permit.longitude || '';
        if (!updates.section) updates.section = permit.section || '';
        if (!updates.township) updates.township = permit.township || '';
        if (!updates.range_field) updates.range_field = permit.range || '';
      }
      if (estimate?.well_info) {
        const wi = estimate.well_info;
        updates.total_well_depth = wi.well_depth || '';
        // Casing — copy casing_sections directly (same shape as the Estimate
        // wizard, "para que ambos hablen el mismo idioma"), fall back to old
        // single-value fields for older estimates.
        if (wi.casing_sections && wi.casing_sections.length > 0) {
          updates.casing_sections = wi.casing_sections.map(s => ({
            from_depth: s.from_depth ?? 0, to_depth: s.to_depth ?? 0,
            diameter: s.diameter || '2', material: s.material || 'pvc',
          }));
          const deepest = wi.casing_sections[wi.casing_sections.length - 1];
          updates.cased_depth = deepest.to_depth || '';
          updates.primary_casing_rows = wi.casing_sections.map(s => ({ ...emptyCasingRow(), diameter: s.diameter || '' }));
          updates.casing_material = wi.casing_sections[0].material || 'pvc';
        } else {
          updates.cased_depth = wi.casing_to || wi.casing_depth || '';
          updates.casing_sections = [{ from_depth: 0, to_depth: updates.cased_depth || 0, diameter: wi.casing_diameter || '2', material: wi.casing_material || 'pvc' }];
          if (wi.casing_diameter) {
            updates.primary_casing_rows = [{ ...emptyCasingRow(), diameter: wi.casing_diameter }];
          }
          updates.casing_material = wi.casing_material || 'pvc';
        }
        updates.drill_method = wi.construction_method || 'rotary';
        if (wi.well_use && (!updates.intended_uses || updates.intended_uses.length === 0)) {
          updates.intended_uses = [wi.well_use];
        }
        // Completion tail — copy completion_intervals directly (same shape as Estimate)
        if (wi.completion_intervals && wi.completion_intervals.length > 0) {
          updates.completion_intervals = wi.completion_intervals.map(iv => ({
            type: iv.type === 'open_hole' ? 'open_hole' : 'screen',
            from_depth: iv.from_depth ?? 0, to_depth: iv.to_depth ?? 0,
          }));
          const screenInterval = wi.completion_intervals.find(i => i.type === 'screen');
          const openHoleInterval = wi.completion_intervals.find(i => i.type === 'open_hole');
          if (screenInterval) {
            updates.screen_from = screenInterval.from_depth?.toString() || '';
            updates.screen_to = screenInterval.to_depth?.toString() || '';
          }
          if (openHoleInterval) {
            updates.open_hole_from = openHoleInterval.from_depth?.toString() || '';
            updates.open_hole_to = openHoleInterval.to_depth?.toString() || '';
          }
        } else if (wi.screen_from || wi.screen_to) {
          updates.screen_from = wi.screen_from;
          updates.screen_to = wi.screen_to;
          updates.completion_intervals = [{ type: 'screen', from_depth: wi.screen_from || 0, to_depth: wi.screen_to || 0 }];
        }
      }
      if (job) {
        updates.completion_date = job.completed_at ? job.completed_at.split('T')[0] : '';
        // Driller's as-built numbers from the Cutting Log override the
        // estimate's original design intent (Alian: "tiene que venir del
        // cutting log y de la info que entre el driller").
        const summary = job.cutting_log_summary || {};
        if (summary.well_depth) updates.total_well_depth = summary.well_depth;
        if (summary.drilled_depth) updates.drilled_depth = summary.drilled_depth;
        if (summary.casing_depth) {
          const sections = (updates.casing_sections && updates.casing_sections.length ? [...updates.casing_sections] : [emptyCasingSection()]);
          sections[sections.length - 1] = { ...sections[sections.length - 1], to_depth: summary.casing_depth };
          updates.casing_sections = sections;
          updates.cased_depth = summary.casing_depth;
        }
        // Auto-fill Drill Cuttings Log from the job's structured cutting_log when present.
        if (Array.isArray(job.cutting_log) && job.cutting_log.length > 0) {
          const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, '_');
          updates.drill_cuttings = job.cutting_log.map(r => ({
            from: r.from ?? '',
            to: r.to ?? '',
            color: norm(r.color) || 'na',
            grain_size: norm(r.grain_size) || 'na',
            material: norm(r.material) || '',
          }));
        }
      }
    }
    setForm(prev => ({ ...prev, ...updates }));
  }, [permit, project, contact, estimate, job]);

  const u = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const toggleUse = (use) => {
    setForm(prev => ({
      ...prev,
      intended_uses: (prev.intended_uses || []).includes(use)
        ? prev.intended_uses.filter(u => u !== use)
        : [...(prev.intended_uses || []), use]
    }));
  };

  const addCutting = () => {
    const lastRow = form.drill_cuttings[form.drill_cuttings.length - 1];
    const newFrom = lastRow?.to || '';
    setForm(prev => ({ ...prev, drill_cuttings: [...prev.drill_cuttings, { from: newFrom, to: '', color: 'na', grain_size: 'na', material: '' }] }));
  };
  const removeCutting = (idx) => setForm(prev => ({ ...prev, drill_cuttings: prev.drill_cuttings.filter((_, i) => i !== idx) }));
  const updateCutting = (idx, field, val) => {
    setForm(prev => {
      const cuttings = [...prev.drill_cuttings];
      cuttings[idx] = { ...cuttings[idx], [field]: val };
      return { ...prev, drill_cuttings: cuttings };
    });
  };

  const addRow = (key, factory) => setForm(prev => ({ ...prev, [key]: [...prev[key], factory()] }));
  const removeRow = (key, idx) => setForm(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  const updateRow = (key, idx, field, val) => {
    setForm(prev => {
      const rows = [...prev[key]];
      rows[idx] = { ...rows[idx], [field]: val };
      return { ...prev, [key]: rows };
    });
  };

  const handleSave = () => {
    // Keep the legacy flat fields (cased_depth / open_hole_* / screen_*)
    // in sync from the new arrays for any other reader relying on them.
    const sections = form.casing_sections || [];
    const deepest = sections[sections.length - 1] || {};
    const screenIv = (form.completion_intervals || []).find(i => i.type === 'screen') || {};
    const openIv = (form.completion_intervals || []).find(i => i.type === 'open_hole') || {};
    const payload = {
      ...form,
      cased_depth: deepest.to_depth ?? form.cased_depth,
      casing_diameter: deepest.diameter ?? form.casing_diameter,
      screen_from: screenIv.from_depth ?? '',
      screen_to: screenIv.to_depth ?? '',
      open_hole_from: openIv.from_depth ?? '',
      open_hole_to: openIv.to_depth ?? '',
    };
    if (onSave) onSave(payload);
  };

  const CasingTable = ({ label, stateKey, maxRows = 5 }) => (
    <div className="space-y-2">
      <p className="text-xs font-medium text-gray-700">{label}</p>
      <div className="grid grid-cols-11 gap-1 text-[10px] font-semibold text-gray-500 uppercase px-1">
        <span className="col-span-2">Dia (in)</span>
        <span className="col-span-2">From (ft)</span>
        <span className="col-span-2">To (ft)</span>
        <span className="col-span-1">Bags</span>
        <span className="col-span-3">Seal Material</span>
        <span className="col-span-1"></span>
      </div>
      {form[stateKey].map((row, idx) => (
        <div key={idx} className="grid grid-cols-11 gap-1 items-center" data-testid={`cr-${stateKey}-${idx}`}>
          <Input className="col-span-2 h-8 text-xs" value={row.diameter} onChange={e => updateRow(stateKey, idx, 'diameter', e.target.value)} />
          <Input className="col-span-2 h-8 text-xs" value={row.from} onChange={e => updateRow(stateKey, idx, 'from', e.target.value)} />
          <Input className="col-span-2 h-8 text-xs" value={row.to} onChange={e => updateRow(stateKey, idx, 'to', e.target.value)} />
          <Input className="col-span-1 h-8 text-xs" value={row.bags} onChange={e => updateRow(stateKey, idx, 'bags', e.target.value)} />
          <Select value={row.seal_material} onValueChange={v => updateRow(stateKey, idx, 'seal_material', v)}>
            <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{SEAL_MATERIALS.map(m => <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1).replace('_', ' ')}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-red-400" onClick={() => removeRow(stateKey, idx)} disabled={form[stateKey].length <= 1}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}
      {form[stateKey].length < maxRows && (
        <Button type="button" variant="outline" size="sm" onClick={() => addRow(stateKey, emptyCasingRow)} className="w-full">
          <Plus className="w-3 h-3 mr-1" /> Add Row
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="completion-report-form">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#003366]" /> Well Completion Report
        </h2>
      </div>

      <Section title="1. Permit & Identification" icon={FileText}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Water Management District</Label>
            <Select value={form.wmd} onValueChange={v => u('wmd', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{WMDS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Permit Number *</Label><Input value={form.permit_number} onChange={e => u('permit_number', e.target.value)} data-testid="cr-permit-num" /></div>
          <div className="space-y-1"><Label className="text-xs">CUP/WUP Number</Label><Input value={form.cup_wup_number} onChange={e => u('cup_wup_number', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">DID Number</Label><Input value={form.did_number} onChange={e => u('did_number', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Florida Unique ID</Label><Input value={form.florida_unique_id} onChange={e => u('florida_unique_id', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">62-524 Delineation No.</Label><Input value={form.delineation_number} onChange={e => u('delineation_number', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Wells Constructed/Repaired/Abandoned *</Label><Input type="number" value={form.wells_constructed} onChange={e => u('wells_constructed', e.target.value)} min="0" /></div>
          <div className="space-y-1"><Label className="text-xs">Wells NOT Constructed/Repaired/Abandoned</Label><Input type="number" value={form.wells_not_constructed} onChange={e => u('wells_not_constructed', e.target.value)} min="0" /></div>
        </div>
      </Section>

      <Section title="2. Owner & Completion" icon={FileText}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Owner Name *</Label><Input value={form.owner_name} onChange={e => u('owner_name', e.target.value)} data-testid="cr-owner" /></div>
          <div className="space-y-1"><Label className="text-xs">Owner Email</Label><Input type="email" value={form.owner_email} onChange={e => u('owner_email', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Completion Date *</Label><Input type="date" value={form.completion_date} onChange={e => u('completion_date', e.target.value)} data-testid="cr-date" /></div>
        </div>
      </Section>

      <Section title="3. Well Location" icon={MapPin}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1"><Label className="text-xs">Well Address *</Label><Input value={form.well_address} onChange={e => u('well_address', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">County *</Label><Input value={form.well_county} onChange={e => u('well_county', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Section *</Label><Input value={form.section} onChange={e => u('section', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Township *</Label><Input value={form.township} onChange={e => u('township', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Range *</Label><Input value={form.range_field} onChange={e => u('range_field', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Land Grant</Label><Input value={form.land_grant} onChange={e => u('land_grant', e.target.value)} /></div>
        </div>
      </Section>

      <Section title="4. Coordinates & Data Source" icon={MapPin}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Latitude</Label><Input value={form.latitude} onChange={e => u('latitude', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Longitude</Label><Input value={form.longitude} onChange={e => u('longitude', e.target.value)} /></div>
          <div className="space-y-1">
            <Label className="text-xs">Data Obtained From</Label>
            <Select value={form.data_source} onValueChange={v => u('data_source', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{DATA_SOURCES.map(s => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Datum</Label>
            <Select value={form.datum} onValueChange={v => u('datum', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{DATUMS.map(d => <SelectItem key={d} value={d}>{d.toUpperCase().replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="5. Type of Work *" icon={Wrench}>
        <div className="flex flex-wrap gap-2">
          {WORK_TYPES.map(wt => (
            <button key={wt} type="button" onClick={() => u('work_type', wt)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.work_type === wt ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              {wt.charAt(0).toUpperCase() + wt.slice(1)}
            </button>
          ))}
        </div>
      </Section>

      <Section title="6. Intended Use(s) of Well(s) *" icon={Droplets}>
        <div className="grid grid-cols-2 gap-1.5">
          {INTENDED_USES.map(use => (
            <button key={use} type="button" onClick={() => toggleUse(use)}
              className={`text-left px-2.5 py-1.5 rounded text-xs border transition-all ${(form.intended_uses || []).includes(use) ? 'bg-[#003366]/10 text-[#003366] border-[#003366]/30 font-medium' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
              {INTENDED_USE_LABELS[use] || use}
            </button>
          ))}
        </div>
        {(form.intended_uses || []).includes('other') && (
          <div className="space-y-1 mt-2">
            <Label className="text-xs">Other - Describe</Label>
            <Input value={form.other_use_description} onChange={e => u('other_use_description', e.target.value)} placeholder="Describe other use..." />
          </div>
        )}
      </Section>

      <Section title="7. Drill Method *" icon={Wrench}>
        <div className="flex flex-wrap gap-2">
          {DRILL_METHODS.map(m => (
            <button key={m} type="button" onClick={() => u('drill_method', m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.drill_method === m ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              {DRILL_METHOD_LABELS[m]}
            </button>
          ))}
        </div>
      </Section>

      <Section title="8. Water Level Measurements" icon={Droplets}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Static Water Level (ft) *</Label><Input value={form.static_water_level} onChange={e => u('static_water_level', e.target.value)} data-testid="cr-static-level" /></div>
          <div className="space-y-1"><Label className="text-xs">Pumping Water Level (ft)</Label><Input value={form.pumping_water_level} onChange={e => u('pumping_water_level', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">After (Hours)</Label><Input value={form.pumping_hours} onChange={e => u('pumping_hours', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">GPM</Label><Input value={form.pumping_gpm} onChange={e => u('pumping_gpm', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Measuring Point (Describe) *</Label><Input value={form.measuring_point} onChange={e => u('measuring_point', e.target.value)} placeholder="Top of casing" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">Ft</Label><Input value={form.measuring_point_ft} onChange={e => u('measuring_point_ft', e.target.value)} /></div>
            <div className="space-y-1">
              <Label className="text-xs">Above/Below</Label>
              <Select value={form.measuring_above_below} onValueChange={v => u('measuring_above_below', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="above">Above Land Surface</SelectItem>
                  <SelectItem value="below">Below Land Surface</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Flowing? *</Label>
            <Select value={form.is_flowing} onValueChange={v => u('is_flowing', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="9. Casing Material *" icon={Wrench}>
        <div className="flex flex-wrap gap-2">
          {CASING_MATERIALS.map(m => (
            <button key={m} type="button" onClick={() => u('casing_material', m)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.casing_material === m ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              {CASING_MATERIAL_LABELS[m]}
            </button>
          ))}
        </div>
      </Section>

      <Section title="10. Well Construction Details *" icon={Ruler}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Total Well Depth (ft) *</Label><Input value={form.total_well_depth} onChange={e => u('total_well_depth', e.target.value)} data-testid="cr-depth" /></div>
          <div className="space-y-1"><Label className="text-xs">Drilled Depth (ft)</Label><Input value={form.drilled_depth} onChange={e => u('drilled_depth', e.target.value)} data-testid="cr-drilled-depth" /></div>
        </div>

        {/* Casing — from/to boxes + diameter/material, "Agregar intervalo" for
            telescopic wells. Same shape as the Estimate wizard's casing_sections
            so both "speak the same language" (Alian). */}
        <div className="space-y-1.5 p-2.5 rounded-lg border border-gray-200 bg-gray-50/60">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-gray-500 flex items-center gap-1.5">
              Casing Depth
              {(form.casing_sections || []).length > 1 && (
                <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">Telescopic Well</span>
              )}
            </Label>
            <button type="button" onClick={() => addRow('casing_sections', emptyCasingSection)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium" data-testid="cr-add-casing-interval">
              <Plus className="w-3 h-3" /> Agregar intervalo
            </button>
          </div>
          <div className="grid grid-cols-[80px_130px_1fr_1fr_24px] gap-1.5 text-[10px] font-semibold text-gray-400 uppercase px-1">
            <span>Dia (in)</span><span>Material</span><span className="text-center">From (ft)</span><span className="text-center">To (ft)</span><span></span>
          </div>
          <div className="space-y-1.5">
            {(form.casing_sections || []).map((sec, idx) => (
              <div key={idx} className="grid grid-cols-[80px_130px_1fr_1fr_24px] gap-1.5 items-center bg-white rounded-lg px-1.5 py-1.5 border border-gray-200" data-testid={`cr-casing-section-${idx}`}>
                <Select value={sec.diameter || '2'} onValueChange={v => updateRow('casing_sections', idx, 'diameter', v)}>
                  <SelectTrigger className="h-8 bg-white text-xs" data-testid={`cr-casing-${idx}-diameter`}><SelectValue /></SelectTrigger>
                  <SelectContent>{['2', '4', '6', '8', '10', '12', '16'].map(d => <SelectItem key={d} value={d}>{d}"</SelectItem>)}</SelectContent>
                </Select>
                <Select value={sec.material || 'pvc'} onValueChange={v => updateRow('casing_sections', idx, 'material', v)}>
                  <SelectTrigger className="h-8 bg-white text-xs" data-testid={`cr-casing-${idx}-material`}><SelectValue /></SelectTrigger>
                  <SelectContent>{CASING_MATERIALS.map(m => <SelectItem key={m} value={m}>{CASING_MATERIAL_LABELS[m]}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="h-8 text-center text-xs" type="number" value={sec.from_depth} onChange={e => updateRow('casing_sections', idx, 'from_depth', e.target.value)} data-testid={`cr-casing-${idx}-from`} />
                <Input className="h-8 text-center text-xs" type="number" value={sec.to_depth} onChange={e => updateRow('casing_sections', idx, 'to_depth', e.target.value)} data-testid={`cr-casing-${idx}-to`} />
                {(form.casing_sections || []).length > 1 ? (
                  <button type="button" onClick={() => removeRow('casing_sections', idx)} className="text-gray-400 hover:text-red-500" data-testid={`cr-casing-${idx}-remove`}>
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                ) : <span />}
              </div>
            ))}
          </div>
        </div>

        {/* Completion tail — Screen or Open Hole, same shape as the Estimate's completion_intervals */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-gray-500">Terminación (Screen / Open Hole) *</Label>
            <button type="button" onClick={() => addRow('completion_intervals', emptyCompletionInterval)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium" data-testid="cr-add-completion-interval">
              <Plus className="w-3 h-3" /> Agregar intervalo
            </button>
          </div>
          <div className="space-y-2">
            {(form.completion_intervals || []).map((iv, idx) => (
              <div key={idx} className="flex items-center gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200" data-testid={`cr-completion-interval-${idx}`}>
                <div className="flex gap-1 flex-shrink-0">
                  <button type="button" onClick={() => updateRow('completion_intervals', idx, 'type', 'screen')}
                    className={`px-2.5 h-7 rounded text-xs font-medium border transition-all ${iv.type === 'screen' ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                    data-testid={`cr-interval-${idx}-screen-btn`}>Screen</button>
                  <button type="button" onClick={() => updateRow('completion_intervals', idx, 'type', 'open_hole')}
                    className={`px-2.5 h-7 rounded text-xs font-medium border transition-all ${iv.type === 'open_hole' ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                    data-testid={`cr-interval-${idx}-openhole-btn`}>Open Hole</button>
                </div>
                <Input className="h-7 w-20 text-center bg-white text-xs" type="number" value={iv.from_depth} onChange={e => updateRow('completion_intervals', idx, 'from_depth', e.target.value)} placeholder="From" data-testid={`cr-interval-${idx}-from`} />
                <span className="text-gray-400 text-xs flex-shrink-0">—</span>
                <Input className="h-7 w-20 text-center bg-white text-xs" type="number" value={iv.to_depth} onChange={e => updateRow('completion_intervals', idx, 'to_depth', e.target.value)} placeholder="To" data-testid={`cr-interval-${idx}-to`} />
                <span className="text-xs text-gray-400 flex-shrink-0">ft</span>
                {(form.completion_intervals || []).length > 1 && (
                  <button type="button" onClick={() => removeRow('completion_intervals', idx)} className="ml-auto text-gray-400 hover:text-red-500 flex-shrink-0" data-testid={`cr-interval-${idx}-remove`}>
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="space-y-1 max-w-[160px]"><Label className="text-xs">Slot Size</Label><Input value={form.slot_size} onChange={e => u('slot_size', e.target.value)} /></div>
        </div>
      </Section>

      {/* WELL LOG - Structured geological log filled during drilling */}
      <Section title="Well Log *" icon={Ruler}>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3">
          <p className="text-xs text-blue-700">
            Record geological formations encountered during drilling. Each row represents a depth interval.
            Add rows as needed for formation changes.
          </p>
        </div>

        {/* Desktop header (hidden on mobile) */}
        <div className="hidden sm:grid grid-cols-12 gap-1.5 text-[10px] font-semibold text-gray-500 uppercase px-1 mb-1">
          <span className="col-span-1">From</span>
          <span className="col-span-1">To</span>
          <span className="col-span-2">Color</span>
          <span className="col-span-3">Grain Size</span>
          <span className="col-span-4">Material</span>
          <span className="col-span-1"></span>
        </div>

        <div className="space-y-2" data-testid="well-log-entries">
          {form.drill_cuttings.map((cut, idx) => (
            <div key={idx} data-testid={`well-log-row-${idx}`}>
              {/* Desktop layout */}
              <div className="hidden sm:grid grid-cols-12 gap-1.5 items-center">
                <Input
                  className="col-span-1 h-9 text-xs text-center"
                  type="number"
                  value={cut.from}
                  onChange={e => updateCutting(idx, 'from', e.target.value)}
                  placeholder="0"
                  data-testid={`well-log-from-${idx}`}
                />
                <Input
                  className="col-span-1 h-9 text-xs text-center"
                  type="number"
                  value={cut.to}
                  onChange={e => updateCutting(idx, 'to', e.target.value)}
                  placeholder="10"
                  data-testid={`well-log-to-${idx}`}
                />
                <Select value={cut.color || 'na'} onValueChange={v => updateCutting(idx, 'color', v)}>
                  <SelectTrigger className="col-span-2 h-9 text-xs" data-testid={`well-log-color-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WELL_LOG_COLORS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={cut.grain_size || 'na'} onValueChange={v => updateCutting(idx, 'grain_size', v)}>
                  <SelectTrigger className="col-span-3 h-9 text-xs" data-testid={`well-log-grain-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(GRAIN_SIZE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={cut.material || ''} onValueChange={v => updateCutting(idx, 'material', v)}>
                  <SelectTrigger className="col-span-4 h-9 text-xs" data-testid={`well-log-material-${idx}`}><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent>
                    {WELL_LOG_MATERIALS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9 w-9 text-red-400 hover:text-red-600" onClick={() => removeCutting(idx)} disabled={form.drill_cuttings.length <= 1}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>

              {/* Mobile layout - card style */}
              <div className="sm:hidden border border-gray-200 rounded-lg p-3 space-y-2 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">Layer {idx + 1}</span>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removeCutting(idx)} disabled={form.drill_cuttings.length <= 1}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-gray-500 uppercase">From (ft)</Label>
                    <Input className="h-9 text-sm" type="number" value={cut.from} onChange={e => updateCutting(idx, 'from', e.target.value)} placeholder="0" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-gray-500 uppercase">To (ft)</Label>
                    <Input className="h-9 text-sm" type="number" value={cut.to} onChange={e => updateCutting(idx, 'to', e.target.value)} placeholder="10" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-gray-500 uppercase">Color</Label>
                    <Select value={cut.color || 'na'} onValueChange={v => updateCutting(idx, 'color', v)}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{WELL_LOG_COLORS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-gray-500 uppercase">Grain Size</Label>
                    <Select value={cut.grain_size || 'na'} onValueChange={v => updateCutting(idx, 'grain_size', v)}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(GRAIN_SIZE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-gray-500 uppercase">Material</Label>
                  <Select value={cut.material || ''} onValueChange={v => updateCutting(idx, 'material', v)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select material" /></SelectTrigger>
                    <SelectContent>{WELL_LOG_MATERIALS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={addCutting} className="w-full mt-2" data-testid="well-log-add-row">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Layer
        </Button>

        {form.drill_cuttings.length > 1 && (
          <div className="mt-3 p-2 bg-gray-50 rounded-lg text-xs text-gray-500 text-center">
            {form.drill_cuttings.length} layers recorded &middot; {form.drill_cuttings[0]?.from || '0'} ft to {form.drill_cuttings[form.drill_cuttings.length - 1]?.to || '?'} ft
          </div>
        )}
      </Section>

      {form.work_type === 'abandonment' && (
        <Section title="11. Abandonment Details" icon={Wrench}>
          <div className="space-y-2">
            <div className="grid grid-cols-9 gap-1 text-[10px] font-semibold text-gray-500 uppercase px-1">
              <span className="col-span-2">From (ft)</span>
              <span className="col-span-2">To (ft)</span>
              <span className="col-span-1">Bags</span>
              <span className="col-span-3">Seal Material</span>
              <span className="col-span-1"></span>
            </div>
            {form.abandonment_rows.map((row, idx) => (
              <div key={idx} className="grid grid-cols-9 gap-1 items-center" data-testid={`cr-abandon-${idx}`}>
                <Input className="col-span-2 h-8 text-xs" value={row.from} onChange={e => updateRow('abandonment_rows', idx, 'from', e.target.value)} />
                <Input className="col-span-2 h-8 text-xs" value={row.to} onChange={e => updateRow('abandonment_rows', idx, 'to', e.target.value)} />
                <Input className="col-span-1 h-8 text-xs" value={row.bags} onChange={e => updateRow('abandonment_rows', idx, 'bags', e.target.value)} />
                <Select value={row.seal_material} onValueChange={v => updateRow('abandonment_rows', idx, 'seal_material', v)}>
                  <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{SEAL_MATERIALS.map(m => <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1).replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8 text-red-400" onClick={() => removeRow('abandonment_rows', idx)} disabled={form.abandonment_rows.length <= 1}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
            {form.abandonment_rows.length < 5 && (
              <Button type="button" variant="outline" size="sm" onClick={() => addRow('abandonment_rows', emptyAbandonmentRow)} className="w-full">
                <Plus className="w-3 h-3 mr-1" /> Add Row
              </Button>
            )}
          </div>
        </Section>
      )}

      <Section title="12. Surface Casing *" icon={Wrench} defaultOpen={false}>
        <CasingTable label="Surface Casing Diameter and Depth" stateKey="surface_casing_rows" maxRows={2} />
      </Section>

      <Section title="13. Primary Casing *" icon={Wrench} defaultOpen={false}>
        <CasingTable label="Primary Casing Diameter and Depth" stateKey="primary_casing_rows" maxRows={5} />
      </Section>

      <Section title="14. Liner Casing" icon={Wrench} defaultOpen={false}>
        <CasingTable label="Liner Casing Diameter and Depth" stateKey="liner_casing_rows" maxRows={3} />
      </Section>

      <Section title="15. Telescope Casing" icon={Wrench} defaultOpen={false}>
        <CasingTable label="Telescope Casing Diameter and Depth" stateKey="telescope_casing_rows" maxRows={3} />
      </Section>

      <Section title="16. Pump Information" icon={Wrench} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Pump Type</Label>
            <Select value={form.pump_type} onValueChange={v => u('pump_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PUMP_TYPES.map(pt => <SelectItem key={pt} value={pt}>{pt.charAt(0).toUpperCase() + pt.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Horsepower</Label><Input value={form.pump_hp} onChange={e => u('pump_hp', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Pump Capacity (GPM)</Label><Input value={form.pump_capacity} onChange={e => u('pump_capacity', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Pump Depth (ft)</Label><Input value={form.pump_depth} onChange={e => u('pump_depth', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Intake Depth (ft)</Label><Input value={form.intake_depth} onChange={e => u('intake_depth', e.target.value)} data-testid="cr-intake-depth" /></div>
        </div>
      </Section>

      <Section title="17. Chemical Analysis" icon={Droplets} defaultOpen={false}>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1"><Label className="text-xs">Iron (ppm)</Label><Input value={form.iron_ppm} onChange={e => u('iron_ppm', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Sulfate (ppm)</Label><Input value={form.sulfate_ppm} onChange={e => u('sulfate_ppm', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Chloride (ppm)</Label><Input value={form.chloride_ppm} onChange={e => u('chloride_ppm', e.target.value)} /></div>
        </div>
        <div className="space-y-1 mt-2">
          <Label className="text-xs">Test Type</Label>
          <Select value={form.test_type} onValueChange={v => u('test_type', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lab">Laboratory Test</SelectItem>
              <SelectItem value="field">Field Test Kit</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Section title="19. Contractor Information" icon={Shield}>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-2">
          <p className="text-xs text-blue-600 font-medium">Auto-filled from company settings</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Contractor Name *</Label><Input value={form.contractor_name} onChange={e => u('contractor_name', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">License Number *</Label><Input value={form.contractor_license} onChange={e => u('contractor_license', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={form.contractor_email} onChange={e => u('contractor_email', e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Driller's Name (Print or Type) *</Label><Input value={form.driller_name} onChange={e => u('driller_name', e.target.value)} placeholder="Driller's name" data-testid="cr-driller-name" /></div>
        </div>
      </Section>

      <div className="space-y-1">
        <Label className="text-xs">Comments / Additional Notes</Label>
        <Textarea value={form.additional_notes} onChange={e => u('additional_notes', e.target.value)} rows={2} placeholder="Additional notes or comments..." />
      </div>

      <div className="flex gap-2 justify-end pt-2 border-t">
        {onClose && <Button type="button" variant="outline" onClick={onClose}>Close</Button>}
        {onSave && (
          <Button type="button" onClick={handleSave} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="cr-save-btn">
            <Save className="w-4 h-4 mr-2" /> Save Completion Report
          </Button>
        )}
      </div>
    </div>
  );
}
